
import React, { useEffect, useState } from "react";
import Breadcrums from "../../../components/breadcrums";
import { rtAccountRecievableBreadcrums } from "../../../constants/breadcrums";
import { paymentsCommands, sectionClass } from "../../../constants/common";
import PageTitle from "../../../components/pageTitle";
import { ColumnDef } from "@tanstack/react-table";
import { getFinanceReportDataByRT } from "../../../api/finance-api";
import CommonFilters from "../../../blocks/commonFilters";
import { listRtEmployees } from "../../../api/consultants-api";
import Currency from "react-currency-formatter";
import classNames from "classnames";
import TanstackTable from "../../../components/TanstackTable";
import { FinanceRecord } from "../../../model";
import { NumericFormat } from "react-number-format";

// Utility function to sort by name ascending and month descending
function sortByNameAndMonth(data: any[]) {
    return data.slice().sort((a, b) => {
        // Sort by name ascending
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        // If names are equal, sort by month descending
        if (a.month < b.month) return 1;
        if (a.month > b.month) return -1;
        return 0;
    });
}

export default function AccountReceivableRt() {
    const [isLoading, setLoading] = React.useState(false);

    const [people, setPeople] = useState<any[]>([]);

    const [data, setData] = React.useState<FinanceRecord[]>([]);

    const [showUnpaidOnly, setShowUnpaidOnly] = React.useState(false);
    const filteredData = showUnpaidOnly ? data.filter(row => Number(row.diff) !== 0) : data;

    const columns = React.useMemo<ColumnDef<FinanceRecord, any>[]>(
        () => [
            {
                accessorKey: "name",
                header: "Resource Name",
                cell: ({ row }) => (
                    <>
                        <span className="font-bold">{row.original.name}</span>
                        {" (" + (row.original.project_name || row.original.project_pid || "RT") + ")"}
                    </>
                ),
                enableColumnFilter: false,
                enableGrouping: false,
                footer: ({ table }) => (
                    <>
                        <span className="font-semibold text-md">Total</span>
                    </>
                ),
            },

            {
                accessorKey: "month",
                header: "Month",
                cell: (info) => info.getValue(),
                enableColumnFilter: false,
                enableGrouping: false,
                footer: ({ table }) => <></>,
            },

            {
                accessorKey: "rate",
                header: "Rate (USD)",
                cell: (info) => <Currency quantity={Number(info.getValue()) || 0} currency="USD" />,
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
            },
            {
                accessorKey: "hrs",
                header: "Efforts (Hrs)",
                cell: (info) => Number(info.getValue()) | 0,
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <>
                        <NumericFormat prefix={""} value={Number(table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.hrs, 0)) || 0} allowLeadingZeros displayType="text" thousandSeparator="," />
                    </>
                ),
            },
            {
                accessorKey: "bill",
                header: "Bill Amount (USD)",
                cell: (info) => <Currency quantity={Number(info.getValue()) || 0} currency="USD" />,
                enableColumnFilter: false,
                enableGrouping: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <>
                        <Currency quantity={Number(table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.bill, 0)) || 0} currency="USD" />
                    </>
                ),
            },
            {
                accessorKey: "payment",
                header: "Amount Received (USD)",
                cell: (info) => <Currency quantity={Number(info.getValue()) || 0} currency="USD" />,
                enableColumnFilter: false,
                enableGrouping: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <>
                        <Currency quantity={Number(table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.payment, 0)) || 0} currency="USD" />
                    </>
                ),
            },
            {
                accessorKey: "balance_hrs",
                header: "Balance Hrs",
                cell: ({ row }) => {
                    const diff = Number(row.original.diff) || 0;
                    if (diff === 0) return 0;
                    const value = Number(row.original.balance_hrs) || 0;
                    return Math.abs(value) < 1e-10 ? 0 : value;
                },
                enableColumnFilter: false,
                enableGrouping: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => {
                    const total = table.getFilteredRowModel().rows.reduce((total, row) => {
                        const diff = Number(row.original.diff) || 0;
                        if (diff === 0) return total;
                        return total + (Number(row.original.balance_hrs) || 0);
                    }, 0);
                    const displayTotal = Math.abs(total) < 1e-10 ? 0 : total;
                    return <>{displayTotal}</>;
                },
            },
            {
                accessorKey: "diff",
                header: "Balance",
                cell: (info) => <Currency quantity={Number(info.getValue()) || 0} currency="USD" />,
                enableColumnFilter: false,
                enableGrouping: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <>
                        <Currency quantity={Number(table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.diff, 0)) || 0} currency="USD" />
                    </>
                ),
            },

            {
                accessorKey: "billMonth",
                header: "Billed Month",
                cell: (info) => info.getValue() || "-",
                enableColumnFilter: false,
                enableGrouping: false,
                footer: ({ table }) => <></>,
            },

            {
                accessorKey: "invoiceDueMonth",
                header: "Due Month",
                cell: (info) => info.getValue() || "-",
                enableColumnFilter: false,
                enableGrouping: false,
                footer: ({ table }) => <></>,
            },
        ],
        []
    );

    useEffect(() => {
        setLoading(true);

        setPeople([]);
        setData([]);

        getFinanceReportDataByRT({
            checkDate: new Date(),
            employee: "",
        }).then((data) => {
            setData(sortByNameAndMonth(data));
        });

        listRtEmployees()
            .then((data) => setPeople(data))
            .finally(() => {
                setLoading(false);
            });
    }, []);

    function applyFilters(filters): any {
        console.log(filters);

        setLoading(true);
        setData([]);

        getFinanceReportDataByRT(filters)
            .then((data) => {
                setData(sortByNameAndMonth(data));
            })
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <main>
            <Breadcrums breadcrumLinks={rtAccountRecievableBreadcrums} commands={paymentsCommands} groups={["Statement"]} module={"Payment"} />
            <PageTitle title="RT Rate Account Receivable Statement " preSummary="Comprehensive Report" summary="Analyzing Account Receivables, Payments, Settlements, and Dues for Strategic Insights."></PageTitle>

            <div className={classNames(sectionClass)}>
                <CommonFilters people={people} applyFilters={applyFilters} clientId={null} />

                {/* Unpaid Invoice Filter Toggle */}
                <div style={{ marginBottom: '1rem' }}>
                    <label>
                        <input
                            type="checkbox"
                            checked={showUnpaidOnly}
                            onChange={() => setShowUnpaidOnly((prev) => !prev)}
                            style={{ marginRight: '0.5rem' }}
                        />
                        Show only unpaid invoices
                    </label>
                </div>

                {filteredData && filteredData.length > 0 && <TanstackTable columns={columns} data={filteredData} downloadBtn={true}></TanstackTable>}
                {!isLoading && filteredData.length <= 0 && <div className="my-5">No records found</div>}
                {isLoading && <div className="my-5">Loading... </div>}
            </div>
        </main>
    );
}
